import { Avatar, Badge, Space } from '@sravni/react-design-system';
import type { BadgeProps } from '@sravni/react-design-system/dist/types/components/Badge';
import { Heading } from '@sravni/react-design-system/lib/Typography';
import { getUuid, useIsMobile } from '@sravni/react-utils';
import { useMemo } from 'react';

import { OFFER_STATUS } from 'constants/offer';

import type { TOffer } from 'shared/types/TRankingService';
import { OfferPopupFeature } from 'shared/ui/OfferPopupFeature/OfferPopupFeature';
import { OrderedList } from 'shared/ui/OrderedList/OrderedList';
import { ParamsTable } from 'shared/ui/ParamsTable/ParamsTable';

import { WhatNextDataWithoutStatus, WhatNextDataWithStatus } from '../config/constants';
import { useGetBranchesQuery } from '../model/fetchBranches.query';
import styles from '../ui/OfferDetailsPopup/OfferDetailsPopup.module.scss';

import { offerDetailsDataTexts } from './OfferDetailsData.texts';

export const useOfferDetailsData = (offer: TOffer, location: ILocationV2) => {
  const isMobile = useIsMobile();
  const { latitude: currentLat, longitude: currentLon } = location;
  const { data: branches } = useGetBranchesQuery({
    id: offer.bankId,
    locationLatitude: currentLat,
    locationLongitude: currentLon,
  });

  const { badges: offerBadges, status, summaries, additionalInfo, logo } = offer || {};
  const { offerConditions, requirements } = additionalInfo || {};

  const mapItems = useMemo(() => {
    const center: [number, number] | undefined =
      typeof currentLat === 'number' && typeof currentLon === 'number' ? [currentLat, currentLon] : undefined;
    const coordinates = (
      (branches?.items as Array<{
        latitude: number;
        longitude: number;
        id: number;
        workTime: string;
        address: string;
      }>) || []
    ).map(({ latitude, longitude, id, workTime, address }) => ({
      latitude,
      longitude,
      description: {
        workTime,
        address,
      },
      uin: id,
    }));
    return { center, coordinates };
  }, [branches, currentLat, currentLon]);

  const badges = useMemo(
    () =>
      offerBadges?.map((item, index) => (
        <Badge
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          variant={item.variant}
          text={item.text}
          color={item.color as BadgeProps['color']}
        />
      )),
    [offerBadges],
  );

  const whatsNext = useMemo(() => {
    const data = status === OFFER_STATUS.NEW ? WhatNextDataWithoutStatus : WhatNextDataWithStatus;

    return (
      <Space
        direction="vertical"
        size={16}
      >
        {isMobile && <Heading level={4}>{offerDetailsDataTexts.whatsNext}</Heading>}
        <OrderedList items={data} />
      </Space>
    );
  }, [isMobile, status]);

  const summary = useMemo(
    () =>
      summaries?.map((item) => (
        <OfferPopupFeature
          className={styles.note}
          key={item?.label || getUuid()}
          label={item?.label || ''}
          value={item?.value || ''}
        />
      )),
    [summaries],
  );

  const requirementsAndDocs = useMemo(() => {
    const requirementsDemands = requirements?.demands;
    const documents = requirements?.documents;

    const data = [];

    if (documents?.length) {
      data.push({ title: 'Документы', items: documents.flat() });
    }

    if (requirementsDemands?.length) {
      data.push({ title: 'Требования к заемщику', items: requirementsDemands });
    }

    return <ParamsTable data={data} />;
  }, [requirements?.demands, requirements?.documents]);

  const conditions = useMemo(() => {
    const conditionsData = offerConditions?.map(({ label, value }) => ({
      title: label,
      items: [value],
    }));

    if (!conditionsData?.length) {
      return null;
    }
    return <ParamsTable data={conditionsData} />;
  }, [offerConditions]);

  const bankIcon = useMemo(
    () => (
      <Avatar
        size={52}
        src={logo}
        className={styles.headerLogo}
      />
    ),
    [logo],
  );

  return {
    mapItems,
    badges,
    whatsNext,
    summary,
    requirementsAndDocs,
    conditions,
    bankIcon,
  };
};
